// 화면 권한 can() — 계약 PERMISSIONS 7 「화면 can()」·설계 2-6 켜는 순서.
// permMode 가 enforce 가 아니면 옛 규칙(accountPermission.permissions)으로 그리고, 새 규칙과 다르면 POST /perm/ui-diff 로 보고만.
// 이번 배포에서 사용자에게 보이는 메뉴는 바뀌지 않는다(enforce 는 서버 모드가 바뀐 날부터).
// admin-hospital·admin-manufacturer 같은 규칙 — 앱마다 다른 것은 PERM_APP 과 맨 아래 MENU_PERMS 표. 순수 모듈(tests/permUi.test.mjs).
export const PERM_APP = "admin-manufacturer";
const PERM_MODES = ["off", "log", "enforce"];

export const can = (perms, code) => Array.isArray(perms) && perms.includes(code);
export const canAny = (perms, codes = []) => codes.some((c) => can(perms, c));

/** 이 앱의 permMode: 문자열이면 그 값, {mode, perServer} 면 perServer[앱] ?? mode. 없거나 모르는 값이면 log(= 옛 규칙, 막지 않는 값) */
export const permModeOf = (modes, app = PERM_APP) => {
  let v = modes && modes.perm;
  if (v && typeof v === "object") v = (v.perServer && v.perServer[app]) || v.mode;
  return PERM_MODES.includes(v) ? v : "log";
};

/** 새 규칙으로 그리는 날인가(enforce + 응답에 perms 가 있음). 아니면 옛 규칙 */
export const shouldUseNewRule = (modes, perms, app = PERM_APP) =>
  permModeOf(modes, app) === "enforce" && Array.isArray(perms);

/** 새 규칙의 메뉴 보임(MENU_PERMS 의 코드 중 하나라도) */
export const menuVisibleNew = (perms, key, table = MENU_PERMS) =>
  !!table[key] && canAny(perms, table[key].codes);

/** 화면에 그릴 값: enforce 면 새 규칙, 아니면 옛 값 그대로 */
export const menuVisible = ({ key, legacy, perms, modes }, table = MENU_PERMS) =>
  shouldUseNewRule(modes, perms) ? menuVisibleNew(perms, key, table) : !!legacy;

/**
 * 옛 규칙과 새 규칙이 다른 메뉴 → POST /perm/ui-diff 본문 {app, screen, code, legacyAllowed, permAllowed} 목록.
 * perms 가 없으면(옛 inapi·W2 합류 전) 비교할 수 없어 [].
 * @param legacyByKey {메뉴 키: 옛 규칙으로 보이나}
 */
export const permUiDiffs = ({ legacyByKey, perms, screen = "sidenav", app = PERM_APP }, table = MENU_PERMS) => {
  if (!Array.isArray(perms)) return [];
  return Object.entries(legacyByKey || {})
    .filter(([key]) => table[key])
    .map(([key, legacy]) => ({
      app,
      screen: `${screen}:${key}`,
      code: table[key].codes[0],
      legacyAllowed: !!legacy,
      permAllowed: menuVisibleNew(perms, key, table),
    }))
    .filter((d) => d.legacyAllowed !== d.permAllowed);
};

/** 계정·코드마다 한 번만 보내려고 localStorage 에 남기는 표시 키(소음 줄이기라 저장소가 비어도 괜찮음) */
export const diffSeenKey = (accountId, code) => `certi.permUiDiff.${accountId || "-"}.${code}`;

// ── 앱마다 다른 표 ─────────────────────────────────────────────
// 제조사 메뉴는 DB(privileges)의 링크로 그려진다 → 메뉴 키마다 그 메뉴가 여는 경로 앞머리(links)와 view 코드(설계 perm.md 2-4,
// 계약 permission-catalog.v1.json 에 있는 코드만). 시험이 links 가 실제 라우트 경로인지 봄.
// 「스튜디오로 이동」(STUDIO_ACCESS)은 코드가 없어져(perm.md 2-4 「없앰」) 표에 넣지 않음. 공지사항은 누구나 → 표 밖.
export const MENU_PERMS = {
  dashboard: { links: ["/dashboard"], codes: ["mfr.dashboard.view"] },
  product: {
    links: ["/item", "/product-list", "/product-certificate-link", "/product-label", "/swift", "/brand-settings", "/gtin-management"],
    codes: ["mfr.product.view"],
  },
  device: { links: ["/product-qr", "/product-device", "/code-management"], codes: ["mfr.device.view"] },
  certificate: { links: ["/certificate", "/certificate-list", "/customer-details"], codes: ["cert.issue.view", "patient.record.view"] },
  hospital: { links: ["/hospital"], codes: ["mfr.hospital.view"] },
  review: { links: ["/hospital/reviews"], codes: ["review.post.view"] },
  contract: { links: ["/contract", "/v2/contract", "/v3/contract", "/v5/contract", "/contract-view-policy"], codes: ["contract.sales.view"] },
  event: { links: ["/events", "/events-v2", "/promotions"], codes: ["event.campaign.view"] },
  sales: { links: ["/salesrecords"], codes: ["commerce.sales.view"] },
  subscription: { links: ["/salesrecords/subscription"], codes: ["billing.payment.view"] },
  // 이용 내역(/audit-logs)은 DB 메뉴가 아니라 직원 계정 조회 화면의 버튼으로만 진입 → 직원 메뉴를 따름
  staff: { links: ["/manufacturer-accounts", "/audit-logs"], codes: ["account.staff.view"] },
  apiKey: { links: ["/api-keys"], codes: ["settings.apikey.view"] },
  companyInfo: { links: ["/info"], codes: ["settings.company.view"] },
  // 공유 링크는 menus 에 줄이 없고(11-01 intest·stg 확인) 브랜드 설정 화면의 「링크 관리」로만 진입 →
  // 입구(entry) 「설정 /brand-settings」 줄이 있으면 이 갈래 줄로 봄(병원 앱이 브랜드 설정 메뉴에 붙인 것과 같음)
  shareLinks: { links: ["/share-links"], entry: "/brand-settings", codes: ["settings.brand.view"] },
  workOrder: { links: ["/certificate-work-order", "/certificate-work-order-list"], codes: ["lab.workorder.view"] },
  // 브라이트몰 주문(DB 메뉴에 없고 회사 id 로 하단 탭에 붙임 — store getVisibleMobileTabs, perm.md 3-3)
  brightmall: { links: ["/brightmall"], codes: ["commerce.mall_order.view"] },
  alimtalk: { links: ["/alimtalk"], codes: ["settings.channel.view", "message.template.view"] },
};

/** 메뉴 링크 → 메뉴 키(경로 마디 단위로 가장 긴 앞머리). 표에 없으면 null */
export const menuKeyOfLink = (link, table = MENU_PERMS) => {
  const path = String(link || "").split(/[?#]/)[0];
  let best = null;
  let bestLen = -1;
  for (const [key, { links }] of Object.entries(table)) {
    for (const p of links) {
      if ((path === p || path.startsWith(`${p}/`)) && p.length > bestLen) {
        best = key;
        bestLen = p.length;
      }
    }
  }
  return best;
};

// 메뉴 줄의 permResource(W2 이관 칸 — 코드 또는 코드 목록). 있으면 표보다 앞섬
const permResourceOf = (item) => {
  const res = item?.permResource;
  return (Array.isArray(res) ? res : res ? [res] : []).filter((c) => typeof c === "string");
};

/** DB 메뉴 줄 → 비교 키(permResource 가 있으면 db:<링크>, 아니면 링크 표). 모르면 null */
export const menuItemKey = (item, table = MENU_PERMS) =>
  permResourceOf(item).length ? `db:${item.link || permResourceOf(item)[0]}` : menuKeyOfLink(item?.link, table);

const linkUnder = (link, prefix) => {
  const path = String(link || "").split(/[?#]/)[0];
  return path === prefix || path.startsWith(`${prefix}/`);
};

/** 지원 접속(서브 관리자 — 서티라이프·리셀러 지원 탭)에서만 여는 화면(11-02, 모든 제조사 공통). 일반 로그인은 회사 메뉴와 상관없이 막음 */
export const isSupportOnlyPath = (path) => linkUnder(path, "/promotions");

/**
 * 운영자 회사 관리자 본인 로그인 토큰인가(role CERTILIFE, accountType 없음 = ADMIN — inapi isOperatorAdmin 과 같은 규칙, 11-03).
 * 이 계정은 지원 전용 화면도 엶. 지원 탭 토큰은 고객사 역할이라 false. 화면 판정만 — 막기는 서버
 */
export const isOperatorAdminToken = (token) => {
  try {
    const claims = JSON.parse(atob(String(token).split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return claims?.role === "CERTILIFE" && (claims.accountType ?? "ADMIN") === "ADMIN";
  } catch (e) {
    return false;
  }
};

/**
 * 이 갈래로 치는 DB 메뉴 줄 — 링크가 그 갈래인 줄 + (entry 가 있으면) 입구 화면 줄을 이 갈래 줄({link: 갈래 첫 링크})로 바꾼 것.
 * 입구 줄은 enforce 날 이 갈래 코드로 판정(브랜드 설정 줄 && settings.brand.view)
 */
const groupItems = (rows, key, table) => [
  ...rows.filter((r) => menuKeyOfLink(r?.link, table) === key),
  ...(table[key]?.entry ? rows.filter((r) => linkUnder(r?.link, table[key].entry)).map(() => ({ link: table[key].links[0] })) : []),
];

/**
 * DB 메뉴(privileges.category[].menu[]) → {legacyByKey, table}: 옛 규칙 = 그 메뉴가 DB 에 있음(entry 갈래는 입구 줄이 있음).
 * extra = DB 밖에서 붙는 메뉴의 옛 값(예: {brightmall: 덴티움 회사인가}).
 */
export const legacyMenus = (categories = [], extra = {}, table = MENU_PERMS) => {
  const legacyByKey = { ...extra };
  const merged = { ...table };
  for (const cat of Array.isArray(categories) ? categories : []) {
    for (const item of cat?.menu || []) {
      const key = menuItemKey(item, table);
      if (!key) continue;
      if (key.startsWith("db:")) merged[key] = { links: [item.link], codes: permResourceOf(item) };
      legacyByKey[key] = true;
    }
  }
  const rows = (Array.isArray(categories) ? categories : []).flatMap((c) => c?.menu || []);
  for (const key of Object.keys(table)) if (table[key].entry && groupItems(rows, key, table).length) legacyByKey[key] = true;
  for (const key of Object.keys(table)) if (!(key in legacyByKey)) legacyByKey[key] = false;
  return { legacyByKey, table: merged };
};

/** DB 메뉴 줄 하나를 그리나(권한 조건만 — DB 에 있음 + enforce 면 새 규칙). 사이드바·하단 탭·라우터가 같이 씀 */
export const menuItemAllowed = (item, { perms, modes } = {}, table = MENU_PERMS) => {
  const key = menuItemKey(item, table);
  return !key || menuVisible({ key, legacy: true, perms, modes }, table);
};

// 주소 판정에서 빼는 갈래 — 브라이트몰 주문은 DB 메뉴가 아니라 회사 id 로 하단 탭에 붙음(권한 조건 아님, store getVisibleMobileTabs)
const ROUTE_UNCHECKED = ["brightmall"];

/** 옛 규칙(DB 메뉴)으로 막을 때 안내(새 권한 코드가 없어 「필요: 이름」을 못 씀) */
export const MENU_DENIED_TEXT = "이 메뉴를 볼 권한이 없습니다. 첫 화면으로 이동했어요.";

/**
 * 주소로 연 화면이 권한 메뉴 갈래(MENU_PERMS 앞머리 — 하위 화면 등록·수정·상세도 앞머리로 따름)인데
 * 그 갈래 메뉴가 사이드바에 하나도 안 보이면 거절 {codes}, 아니면 null. 판정은 사이드바와 같은 menuItemAllowed.
 * categories = 받은 DB 메뉴(privileges.category), 못 받았으면 null → 막지 않음.
 * codes: DB 메뉴엔 있는데 새 규칙(enforce)으로 숨겼으면 그 코드(안내 「필요: 이름」), DB 메뉴에 없으면 null(MENU_DENIED_TEXT)
 */
export const routeDenial = ({ path, categories, perms, modes }, table = MENU_PERMS) => {
  const key = menuKeyOfLink(path, table);
  if (!key || ROUTE_UNCHECKED.includes(key) || !Array.isArray(categories)) return null;
  const items = groupItems(categories.flatMap((c) => c?.menu || []), key, table);
  const { table: merged } = legacyMenus(categories, {}, table);
  if (items.some((item) => menuItemAllowed(item, { perms, modes }, merged))) return null;
  return { codes: items.length ? merged[menuItemKey(items[0], merged)].codes : null };
};
